/**
 * @fileoverview Carousel component
 *
 * Slider carousel
 *
 * ! window.matchMedia Webkit fix - If you need to change any of the matchMedia code read the fix comment and adjust as needed
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import Swipe from '../../utils/swipe';

class Carousel {
	constructor(carousel, arguments_) {
		this.settings = {
			breakpoints: {
				// mobile first
				xs: {
					slidesPerView: arguments_.breakpoints?.xs?.slidesPerView || 1,
					spaceBetween: arguments_.breakpoints?.xs?.spaceBetween || 4
				},
				sm: {
					// when window width is >= 375
					slidesPerView: arguments_.breakpoints?.sm?.slidesPerView || 1,
					spaceBetween: arguments_.breakpoints?.sm?.spaceBetween || 4
				},
				md: {
					// when window width is >= 768
					slidesPerView: arguments_.breakpoints?.md?.slidesPerView || 1,
					spaceBetween: arguments_.breakpoints?.md?.spaceBetween || 4
				},
				xl: {
					// when window width is >= 1200
					slidesPerView: arguments_.breakpoints?.xl?.slidesPerView || 3,
					spaceBetween: arguments_.breakpoints?.xl?.spaceBetween || 20
				}
			},
			currentSlide: arguments_.currentSlide || 0,
			infiniteLoop: arguments_.infiniteLoop || true,
			slidesPerView: arguments_.slidesPerView || 1,
			spaceBetween: arguments_.spaceBetween || 20, // margin right for each slide
			slideSizing: arguments_.slideSizing || 'min-width' // Options: `min-width`, `min-max-width`
		};

		this._carousel = carousel;

		this.initCarousel(carousel);
	}

	initCarousel(carousel) {
		this.slidesWrapper = SelectorEngine.findOne('.lta-carousel__slides-wrapper', carousel);
		this.slides = SelectorEngine.find('.lta-carousel__slide', carousel);
		this.numberOfSlides = SelectorEngine.find('.lta-carousel__slide', carousel).length;
		this.previousButton = SelectorEngine.findOne('.lta-carousel__arrow--previous', carousel);
		this.nextButton = SelectorEngine.findOne('.lta-carousel__arrow--next', carousel);
		this.controlsWrapper = SelectorEngine.findOne('.lta-carousel__controls', carousel);

		this.smMedia = window.matchMedia('(min-width: 375px)');
		this.mdMedia = window.matchMedia('(min-width: 768px)');
		this.xlMedia = window.matchMedia('(min-width: 1200px)');

		const { xl, md, sm, xs } = this.settings.breakpoints;
		if (window.innerWidth >= 1200) {
			this.settings.slidesPerView = xl.slidesPerView;
			this.settings.spaceBetween = xl.spaceBetween;
		} else if (window.innerWidth >= 768) {
			this.settings.slidesPerView = md.slidesPerView;
			this.settings.spaceBetween = md.spaceBetween;
		} else if (window.innerWidth >= 375) {
			this.settings.slidesPerView = sm.slidesPerView;
			this.settings.spaceBetween = sm.spaceBetween;
		} else {
			this.settings.slidesPerView = xs.slidesPerView;
			this.settings.spaceBetween = xs.spaceBetween;
		}

		this.slideWidth = 100 / this.settings.slidesPerView;

		this.setEventListeners();
		this.generateDots();
		this.setSlidesSize();
		this.setActiveSlides();
	}

	handleSlideChange() {
		const translateWidth = -(this.settings.currentSlide * this.slideWidth);
		const translateMargin = (this.settings.spaceBetween / this.settings.slidesPerView) * this.settings.currentSlide;

		this.slidesWrapper.style.transform = `translateX(calc(${translateWidth}% - ${translateMargin}px)`;

		if (this.dots.length > 0) {
			[...this.dots].forEach((dot) => dot.classList.remove('isActive'));
			this.dots[this.settings.currentSlide].classList.add('isActive');
		}

		this.setActiveSlides();
	}

	nextSlide() {
		const condition = this.settings.currentSlide <= this.numberOfSlides - this.settings.slidesPerView - 1;
		if (this.settings.infiniteLoop) {
			this.settings.currentSlide = condition ? this.settings.currentSlide + 1 : 0;
		} else {
			this.settings.currentSlide = condition ? this.settings.currentSlide + 1 : this.numberOfSlides - this.settings.slidesPerView;
		}

		this.handleSlideChange();
	}

	previousSlide() {
		if (this.settings.infiniteLoop) {
			this.settings.currentSlide = this.settings.currentSlide <= 0 ? this.numberOfSlides - this.settings.slidesPerView : this.settings.currentSlide - 1;
		} else {
			this.settings.currentSlide = this.settings.currentSlide <= 0 ? 0 : this.settings.currentSlide - 1;
		}
		this.handleSlideChange();
	}

	setEventListeners() {
		this.nextButton.addEventListener('click', this.nextSlide.bind(this));
		this.previousButton.addEventListener('click', this.previousSlide.bind(this));

		// Safari Webkit has this bug and it fails on iOS 13 because `addEventListener` doesn't work on `matchMedia`.
		// This is me doing a very smart patch here :)
		// https://bugs.webkit.org/show_bug.cgi?id=203288
		if (!this.smMedia.addEventListener) {
			this.smMedia.addEventListener = (type, fn) => this.smMedia.addListener(fn);
			this.mdMedia.addEventListener = (type, fn) => this.smMedia.addListener(fn);
			this.xlMedia.addEventListener = (type, fn) => this.smMedia.addListener(fn);
		}

		this.smMedia.addEventListener('change', (event) => {
			this.breakpointChange(event, this.settings.breakpoints.sm);
		});

		this.mdMedia.addEventListener('change', (event) => {
			this.breakpointChange(event, this.settings.breakpoints.md);
		});

		this.xlMedia.addEventListener('change', (event) => {
			this.breakpointChange(event, this.settings.breakpoints.xl);
		});

		const self = this;
		const swipe = new Swipe(this.carousel, {
			onSwipeLeft() {
				self.nextSlide();
			},
			onSwipeRight() {
				self.previousSlide();
			}
		});

		this._carousel.addEventListener("touchstart", (event) => {
			swipe.swipeStart(event);
		});
		this._carousel.addEventListener("touchmove", (event) => {
			swipe.swipping(event);
		});
		this._carousel.addEventListener("touchend", (event) => {
			swipe.swipeEnd(event);
		});
	}

	breakpointChange(event, nextBreakpoint) {
		const { breakpoints } = this.settings;
		let previousBreakpoint;
		switch (nextBreakpoint) {
			case breakpoints.xl:
				previousBreakpoint = breakpoints.md;
				break;
			case breakpoints.md:
				previousBreakpoint = breakpoints.sm;
				break;
			default:
				previousBreakpoint = breakpoints.xs;
				break;
		}

		if (event.matches) {
			this.settings.slidesPerView = nextBreakpoint.slidesPerView;
			this.settings.spaceBetween = nextBreakpoint.spaceBetween;
		} else {
			this.settings.slidesPerView = previousBreakpoint.slidesPerView;
			this.settings.spaceBetween = previousBreakpoint.spaceBetween;
		}
		this.slideWidth = 100 / this.settings.slidesPerView;

		if (this.numberOfSlides > this.settings.slidesPerView) {
			// to fix bad positioning when changing breakpoints and currentSlide is on the last slides
			if (this.settings.currentSlide >= this.numberOfSlides - (this.settings.slidesPerView - 1)) {
				this.settings.currentSlide = this.numberOfSlides - this.settings.slidesPerView;
			}
		}

		this.setSlidesSize();
		this.handleSlideChange();
		this.generateDots();
	}

	generateDots() {
		const dotsWrapper = SelectorEngine.findOne('.lta-carousel__dots-wrapper', this._carousel);
		const dotsQuantity = this.numberOfSlides - this.settings.slidesPerView + 1;

		// removes all dots (this is used in breakpoint changes)
		while (dotsWrapper.firstElementChild) {
			dotsWrapper.firstElementChild.remove();
		}

		if (dotsQuantity > 1) {
			for (let i = 0; i < dotsQuantity; i++) {
				const dot = document.createElement('button');

				dot.addEventListener('click', () => {
					this.settings.currentSlide = i;
					this.handleSlideChange();
				});

				dot.textContent = `Slide ${i + 1}`;
				dot.classList.add('lta-carousel__dot');

				this.controlsWrapper.style.display = '';
				dotsWrapper.append(dot);
			}
		} else {
			this.controlsWrapper.style.display = 'none';
		}

		// if there are at least one dot, add active class
		dotsWrapper.children[0] && dotsWrapper.children[this.settings.currentSlide].classList.add('isActive');
		this.dots = dotsWrapper.children;
	}

	setSlidesSize() {
		const { spaceBetween, slideSizing, slidesPerView } = this.settings;
		const marginSpacing = spaceBetween - spaceBetween / slidesPerView;

		[...this.slides].forEach((slide) => {
			switch (slideSizing) {
				case 'min-width':
					slide.style.minWidth = `calc(${this.slideWidth}% - ${marginSpacing}px)`;
					break;
				case 'min-max-width':
					slide.style.minWidth = `calc(${this.slideWidth}% - ${marginSpacing}px)`;
					slide.style.maxWidth = `calc(${this.slideWidth}% - ${marginSpacing}px)`;
				default:
					slide.style.minWidth = `calc(${this.slideWidth}% - ${marginSpacing}px)`;
					break;
			}

			slide.style.marginRight = `${spaceBetween}px`;
		});
	}

	// also set aria-hidden true on slides that are not shown on screen
	setActiveSlides() {
		[...this.slides].forEach((slide, index) => {
			if (index >= this.settings.currentSlide && index < this.settings.currentSlide + this.settings.slidesPerView) {
				slide.ariaHidden = 'false';
				slide.classList.add('isActive');
			} else {
				slide.ariaHidden = 'true';
				slide.classList.remove('isActive');
			}
		});

		if (!this.settings.infiniteLoop) {
			// hide left button if first slide is active and right button if last one is active
			if (this.settings.currentSlide === 0) {
				this.previousButton.style.opacity = 0;
				this.previousButton.style.pointerEvents = 'none';
				this.previousButton.ariaHidden = 'true';
				this.previousButton.children[0].tabIndex = '-1';
			} else {
				this.previousButton.style.opacity = '';
				this.previousButton.style.pointerEvents = '';
				this.previousButton.ariaHidden = 'false';
				this.previousButton.children[0].tabIndex = '0';
			}
			if (this.settings.currentSlide + this.settings.slidesPerView === this.slides.length) {
				this.nextButton.style.opacity = 0;
				this.nextButton.style.pointerEvents = 'none';
				this.nextButton.ariaHidden = 'true';
				this.nextButton.children[0].tabIndex = '-1';
			} else {
				this.nextButton.style.opacity = '';
				this.nextButton.style.pointerEvents = '';
				this.nextButton.ariaHidden = 'false';
				this.nextButton.children[0].tabIndex = '0';
			}
		}
	}
}

export default Carousel;
